import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATUpButton from "../../components/Buttons/PATUpButton";
import PATDownButton from "../../components/Buttons/PATDownButton";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import {
	capitalizeFirstLetter,
	daysBetween,
	getCodeDescription,
	getCodeId,
	getRowsPerPageOptions,
	mapDictionaryOptions,
	StatusBadge,
} from "../../utils/functions";
import { Box } from "@mui/system";
import { useTheme } from "@emotion/react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AMSModules, paginationStyle } from "../../utils/constants";
import EquipmentsTableToolbar from "./EquipmentsTableToolbar";
import AddEditEquipmentDialog from "./AddEditEquipmentDialog";
import EquipmentCheckInOutDialog from "./EquipmentCheckInOutDialog";
import QuickAuditDialog from "./QuickAuditDialog";
import QuickAssignDialog from "./QuickAssignDialog";
import {
	getEquipment,
	createEquipment,
	updateEquipment,
	deleteEquipment,
	checkInOutEquipment,
	auditEquipment,
	bulkAssignEquipment,
	updateMaintenanceRecord,
	createMaintenanceRecord,
	getEquipmentById,
} from "../../reducers/EquipmentsReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { getEmployees, getFundingSources } from "../../utils/commonServices";
import { getVehicleOptions } from "../WorkOrders/useWorkOrdersData";
import {
	getBrandOptions,
	getStyleOptions,
	getCaliberOptions,
	getSightsOptions,
	getLocationOptions,
	getPurchasedFromOptions,
} from "./useEquipmentsData";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "desc" }];

export const initialFilterState = {
	type: [],
	status: [],
	condition: [],
	categoryCode: [],
	division: [],
	brand: [],
	style: [],
	caliber: [],
	sights: [],
	location: [],
	purchasedFrom: [],
	perPersonnelId: [],
	vehicleId: [],
	fundingSourceId: [],
	assignmentType: "all", // all, out, in
};

const Equipments = () => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const { equipment, loading, total } = useSelector(
		(state) =>
			state?.equipment || {
				equipment: [],
				loading: false,
				total: 0,
			},
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const { codePerDepartment } = dictionary || {};

	const [search, setSearch] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.equipments || initialFilterState,
	);
	const [openDialog, setOpenDialog] = useState(false);
	const [openCheckDialog, setOpenCheckDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openAuditDialog, setOpenAuditDialog] = useState(false);
	const [quickAssignOpen, setQuickAssignOpen] = useState(false);
	const [selectedEquipment, setSelectedEquipment] = useState(null);
	const [selectedId, setSelectedId] = useState(null);

	// Options state
	const [employees, setEmployees] = useState([]);
	const [vehicles, setVehicles] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [brandOptions, setBrandOptions] = useState([]);
	const [styleOptions, setStyleOptions] = useState([]);
	const [caliberOptions, setCaliberOptions] = useState([]);
	const [sightsOptions, setSightsOptions] = useState([]);
	const [locationOptions, setLocationOptions] = useState([]);
	const [purchasedFromOptions, setPurchasedFromOptions] = useState([]);

	const currentAgency = useRef(null);

	const typeOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentTypes) || [];
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const statusOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentStatus) || [];
	const conditionOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentConditions) || [];
	const categoryOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentCategory) || [];
	const divisionOptions = mapDictionaryOptions(codePerDepartment) || [];
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const serviceRequirements =
		mapDictionaryOptions(dictionary.codePerEquipmentServiceRequirements) ||
		[];

	// Load options
	const handleLoadData = async () => {
		const [
			empList,
			vehicleList,
			fundingList,
			brands,
			styles,
			calibers,
			sights,
			locations,
			vendors,
		] = await Promise.all([
			getEmployees(userAgency, true),
			getVehicleOptions(userAgency),
			getFundingSources(userAgency, true),
			getBrandOptions(userAgency),
			getStyleOptions(userAgency),
			getCaliberOptions(userAgency),
			getSightsOptions(userAgency),
			getLocationOptions(userAgency),
			getPurchasedFromOptions(userAgency),
		]);

		setEmployees(empList);
		setVehicles(vehicleList);
		setFundingSources(fundingList);
		setBrandOptions(brands);
		setStyleOptions(styles);
		setCaliberOptions(calibers);
		setSightsOptions(sights);
		setLocationOptions(locations);
		setPurchasedFromOptions(vendors);
	};

	useEffect(() => {
		if (userAgency && dictionary) {
			handleLoadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, dictionary]);

	useEffect(() => {
		if (selectedEquipment?.id && equipment.length > 0) {
			// Find the updated equipment from Redux store
			const updatedEquipment = equipment.find(
				(eq) => eq.id === selectedEquipment.id,
			);

			// If found, update selectedEquipment
			if (updatedEquipment) {
				setSelectedEquipment(updatedEquipment);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [equipment]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getEquipment(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	// Get employee info for display
	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const employee = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return employee ? employee.label : "—";
		},
		[employees],
	);

	// Get vehicle info for display
	const getVehicleDisplay = useCallback(
		(vehicleId) => {
			const vehicle = vehicles.find((v) => v.value === vehicleId);
			return vehicle ? vehicle.label : vehicleId || "—";
		},
		[vehicles],
	);

	// Helper function to calculate service requirement status for an equipment
	const getEqSvcStatus = (eq, req, maintenanceRecords) => {
		const DUE_SOON_PCT = 0.1;

		const done = (maintenanceRecords || [])
			.filter(
				(m) =>
					m.equipmentId === eq.id &&
					m.status === "Completed" &&
					m.date &&
					m.serviceRequirementId === req.ptsCodeId,
			)
			.sort((a, b) => new Date(b.date) - new Date(a.date));

		const lastDate = done[0]?.date || eq.acquiredDate || null;
		const daysSince = lastDate ? daysBetween(lastDate) : 9999;
		const intervalDays = req.intervalDays || Number(req.Value) || 365;
		const remaining = intervalDays - daysSince;
		const dueSoon = remaining <= intervalDays * DUE_SOON_PCT;

		return {
			status: remaining <= 0 ? "Overdue" : dueSoon ? "Due Soon" : "OK",
			dueIn: remaining,
			daysSince,
			lastServiceDate: lastDate,
		};
	};

	// Get worst service status for equipment
	const getServiceStatus = useCallback(
		(equipment) => {
			const eqServiceReqs = equipment?.serviceRequirements || [];
			const maintenanceRecords = equipment?.maintenanceRecords || [];

			if (!eqServiceReqs.length || !serviceRequirements.length) {
				return { worstSvc: null, svcStatuses: [] };
			}

			const svcStatuses = eqServiceReqs
				.map((ptsCodeId) => {
					// Find the service requirement definition
					const sr = serviceRequirements.find(
						(r) =>
							r.ptsCodeId === ptsCodeId || r.value === ptsCodeId,
					);
					if (!sr) return null;

					const status = getEqSvcStatus(
						equipment,
						sr,
						maintenanceRecords,
					);
					return { sr, ...status };
				})
				.filter(Boolean);

			// Find the worst status (Overdue > Due Soon > OK, then by dueIn)
			const worstSvc = svcStatuses.reduce((worst, current) => {
				if (!worst) return current;

				const priorityMap = { Overdue: 3, "Due Soon": 2, OK: 1 };
				const currentPriority = priorityMap[current.status] || 0;
				const worstPriority = priorityMap[worst.status] || 0;

				if (currentPriority > worstPriority) return current;
				if (
					currentPriority === worstPriority &&
					current.dueIn < worst.dueIn
				)
					return current;

				return worst;
			}, null);

			return { worstSvc, svcStatuses };
		},
		[serviceRequirements],
	);

	const baseColumns = useMemo(
		() => [
			{
				field: "type",
				headerName: "Type",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => (
					<StatusBadge
						status={capitalizeFirstLetter(
							getCodeDescription(
								row?.type || row?.typeInfo?.Code,
								dictionary.codePerEquipmentTypes,
							),
						)}
					/>
				),
			},
			{
				field: "propertyNumber",
				headerName: "Property#",
				width: 130,
				sortable: true,
			},
			{
				field: "serialNumber",
				headerName: "Serial#",
				width: 130,
				sortable: true,
			},
			{
				field: "brand/model",
				headerName: "Brand / Model",
				width: 180,
				sortable: true,
				renderCell: ({ row }) =>
					`${row?.brand || ""} ${row?.model || ""}`.trim() || "—",
			},
			{
				field: "condition",
				headerName: "Condition",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => (
					<StatusBadge
						status={capitalizeFirstLetter(
							getCodeDescription(
								row?.condition || row?.conditionInfo?.Code,
								dictionary.codePerEquipmentConditions,
							),
						)}
					/>
				),
			},
			{
				field: "status",
				headerName: "Status",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => (
					<StatusBadge
						status={capitalizeFirstLetter(
							getCodeDescription(
								row?.status || row?.statusInfo?.Code,
								dictionary.codePerEquipmentStatus,
							),
						)}
					/>
				),
			},
			{
				field: "location",
				headerName: "Location",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => row?.location || "—",
			},
			{
				field: "vehicleId",
				headerName: "Vehicle",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => getVehicleDisplay(row?.vehicleId),
			},
			{
				field: "perPersonnelId",
				headerName: "Employee",
				width: 150,
				sortable: true,
				renderCell: ({ row }) =>
					getEmployeeDisplay(row?.perPersonnelId),
			},
			{
				field: "serviceDue",
				headerName: "Service Due",
				width: 140,
				sortable: false,
				renderCell: ({ row }) => {
					const { worstSvc, svcStatuses } = getServiceStatus(row);

					if (worstSvc) {
						const isOverdue = worstSvc.status === "Overdue";
						const isDueSoon = worstSvc.status === "Due Soon";
						const color = isOverdue
							? "#d32f2f"
							: isDueSoon
								? "#ed6c02"
								: "#2e7d32";

						return (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									lineHeight: 1.2,
								}}
							>
								<Box
									component="span"
									sx={{
										fontSize: 11,
										fontWeight: 700,
										color: color,
										whiteSpace: "nowrap",
									}}
								>
									{isOverdue
										? `${Math.abs(worstSvc.dueIn)}d overdue`
										: `${worstSvc.dueIn}d left`}
								</Box>
								<Box
									component="span"
									sx={{
										fontSize: 10,
										fontWeight: 400,
										color: "text.secondary",
									}}
								>
									{worstSvc.sr?.label ||
										worstSvc.sr?.Code ||
										""}
								</Box>
							</Box>
						);
					}

					if (svcStatuses.length > 0) {
						return (
							<Box
								component="span"
								sx={{
									fontSize: 11,
									fontWeight: 600,
									color: "#2e7d32",
								}}
							>
								OK
							</Box>
						);
					}

					return (
						<Box
							component="span"
							sx={{
								fontSize: 11,
								color: "text.disabled",
							}}
						>
							—
						</Box>
					);
				},
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 150,
				align: "center",
				sortable: false,
				renderCell: ({ row }) => {
					const curStatusDef = statusOptions.find(
						(s) => s.value === row?.status,
					);
					const isOut = curStatusDef?.isCheckedOut === true;
					return row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{isOut ? (
											<PATDownButton
												tooltipText="Check In"
												onClick={(e) => {
													e.stopPropagation();
													setSelectedEquipment(row);
													setOpenCheckDialog(true);
												}}
												size="20px"
											/>
										) : (
											<PATUpButton
												tooltipText="Check Out"
												onClick={(e) => {
													e.stopPropagation();
													setSelectedEquipment(row);
													setOpenCheckDialog(true);
												}}
												size="20px"
											/>
										)}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Equipment"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedEquipment(row);
												setOpenDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Equipment"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedId(row.id);
												setSelectedEquipment(row);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null;
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			statusOptions,
			getVehicleDisplay,
			getEmployeeDisplay,
			getServiceStatus,
			dictionary,
		],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["equipmentsTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["equipmentsTableColumnOrder"]),
				);
			}

			if (settingsMap["equipmentsTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["equipmentsTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["equipmentsTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["equipmentsTableColumnVisibility"]),
				);
			}

			if (settingsMap["equipmentsTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["equipmentsTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"equipmentsTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"equipmentsTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => ({
			field: col.field,
			width: col.width,
		}));
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"equipmentsTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"equipmentsTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		setPage(INITIAL_PAGE);
		dispatch({ type: AMSModules.Equipments, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Equipments, payload: initialFilterState });
	};

	const refreshEquipmentData = useCallback(async () => {
		if (selectedEquipment?.id) {
			try {
				// Option A: Fetch single equipment
				const response = await dispatch(
					getEquipmentById(selectedEquipment.id, userAgency),
				);
				// Check the actual response structure from your reducer
				const updatedEquipment = response?.payload || response;
				if (updatedEquipment) {
					setSelectedEquipment(updatedEquipment);
				}
			} catch (error) {
				console.error("Failed to refresh equipment:", error);
			}
		}
	}, [selectedEquipment?.id, userAgency, dispatch]);

	// Handle add maintenance
	const handleAddMaintenance = async (record) => {
		try {
			await dispatch(
				createMaintenanceRecord(
					record,
					userAgency,
					userData.user.ptsUserID,
				),
			);
			dispatch(
				notify("Maintenance record added successfully", "success"),
			);
		} catch (error) {
			dispatch(notify("Failed to add maintenance record", "error"));
		}
	};

	// Handle edit maintenance
	const handleEditMaintenance = async (record) => {
		try {
			await dispatch(
				updateMaintenanceRecord(
					record.id,
					record,
					userAgency,
					userData.user.ptsUserID,
					record,
				),
			);
			dispatch(
				notify("Maintenance record updated successfully", "success"),
			);
		} catch (error) {
			dispatch(notify("Failed to update maintenance record", "error"));
		}
	};

	// Handle save equipment
	const handleSaveEquipment = async (payload) => {
		try {
			// Extract serviceRequirements from payload
			const { serviceRequirements: svcReqIds, ...equipmentData } =
				payload;

			const data = {
				...equipmentData,
				typeId: getCodeId(
					payload.type,
					dictionary?.codePerEquipmentTypes,
				),
				statusId: getCodeId(
					payload.status,
					dictionary?.codePerEquipmentStatus,
				),
				conditionId: getCodeId(
					payload.condition,
					dictionary?.codePerEquipmentConditions,
				),
				categoryCodeId: getCodeId(
					payload.categoryCode,
					dictionary?.codePerEquipmentCategory,
				),
				divisionId: getCodeId(payload.division, codePerDepartment),
			};

			if (selectedEquipment) {
				await dispatch(
					updateEquipment(
						selectedEquipment.id,
						data,
						svcReqIds || [],
						userAgency,
						userData.user.ptsUserID,
						selectedEquipment,
					),
				);
			} else {
				await dispatch(
					createEquipment(
						data,
						svcReqIds || [],
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}

			setOpenDialog(false);
			setSelectedEquipment(null);
		} catch (error) {
			dispatch(notify("Failed to save equipment", "error"));
		}
	};

	// Handle check-in/out
	const handleCheckInOut = async (payload) => {
		try {
			await dispatch(
				checkInOutEquipment(
					payload.equipmentId,
					payload,
					userAgency,
					userData.user.ptsUserID,
				),
			);
			setOpenCheckDialog(false);
			setSelectedEquipment(null);
			dispatch(
				notify("Equipment checked in/out successfully", "success"),
			);
		} catch (error) {
			dispatch(notify("Failed to check in/out equipment", "error"));
		}
	};

	// Handle delete
	const handleDelete = async () => {
		try {
			await dispatch(
				deleteEquipment(
					selectedId,
					userData.user.ptsUserID,
					selectedEquipment,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedEquipment(null);
			dispatch(notify("Equipment deleted successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to delete equipment", "error"));
		}
	};

	// Handle audit (UPDATED)
	const handleAudit = async (equipmentIds, auditData) => {
		try {
			const auditPayload = {
				items: equipmentIds.map((id) => ({
					equipmentId: id,
					lastInspectionDate: auditData.lastInspectionDate,
				})),
				auditDate: auditData.lastInspectionDate,
			};

			await dispatch(
				auditEquipment(
					auditPayload,
					userAgency,
					userData.user.ptsUserID,
				),
			);
			setOpenAuditDialog(false);
			dispatch(notify("Audit completed successfully", "success"));

			// Refresh list
			const { field, sort } = sortModel[0] || {};
			if (field && sort) {
				dispatch(
					getEquipment(
						page,
						pageSize,
						field,
						sort,
						search,
						filters,
						userAgency,
					),
				);
			}
		} catch (error) {
			dispatch(notify("Failed to complete audit", "error"));
		}
	};

	// Handle bulk assign (UPDATED)
	const handleBulkAssign = async (equipmentIds, assignData) => {
		try {
			const payload = {
				equipmentIds: equipmentIds,
				perPersonnelId:
					assignData.empMode === "assign"
						? assignData.employeeID
						: assignData.empMode === "unassign"
							? null
							: undefined,
				vehicleId:
					assignData.vehMode === "assign"
						? assignData.vehicleID
						: assignData.vehMode === "unassign"
							? null
							: undefined,
				location:
					assignData.locMode === "set"
						? assignData.location
						: assignData.locMode === "clear"
							? null
							: undefined,
				division: assignData.division || undefined,
				status: assignData.status || undefined,
				notes: `Bulk assignment - ${assignData.empMode === "assign" ? "assigned to employee" : assignData.empMode === "unassign" ? "unassigned employee" : "employee unchanged"}`,
			};

			await dispatch(
				bulkAssignEquipment(
					payload,
					userAgency,
					userData.user.ptsUserID,
				),
			);
			setQuickAssignOpen(false);
			dispatch(notify("Equipment assigned successfully", "success"));

			// Refresh list
			const { field, sort } = sortModel[0] || {};
			if (field && sort) {
				dispatch(
					getEquipment(
						page,
						pageSize,
						field,
						sort,
						search,
						filters,
						userAgency,
					),
				);
			}
		} catch (error) {
			dispatch(notify("Failed to assign equipment", "error"));
		}
	};

	// Prepare print data
	const printEquipment = useMemo(() => {
		return equipment.map((eq) => ({
			...eq,
			typeDisplay: eq?.type || eq?.typeInfo?.Code || "—",
			conditionDisplay: eq?.condition || eq?.conditionInfo?.Code || "—",
			statusDisplay: eq?.status || eq?.statusInfo?.Code || "—",
			brandModel: `${eq?.brand || ""} ${eq?.model || ""}`.trim() || "—",
			employee: getEmployeeDisplay(eq?.perPersonnelId),
			vehicle: getVehicleDisplay(eq?.vehicleId),
		}));
	}, [equipment, getEmployeeDisplay, getVehicleDisplay]);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)",
					marginTop: theme.spacing(0.5),
				}}
			>
				<EquipmentsTableToolbar
					openAdd={() => {
						setSelectedEquipment(null);
						setOpenDialog(true);
					}}
					search={search}
					setSearch={setSearch}
					onAuditClick={() => setOpenAuditDialog(true)}
					onAssignClick={() => setQuickAssignOpen(true)}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
					typeOptions={typeOptions}
					statusOptions={statusOptions}
					conditionOptions={conditionOptions}
					categoryOptions={categoryOptions}
					divisionOptions={divisionOptions}
					employeeOptions={employees}
					vehicleOptions={vehicles}
					fundingOptions={fundingSources}
					brandOptions={brandOptions}
					styleOptions={styleOptions}
					caliberOptions={caliberOptions}
					sightsOptions={sightsOptions}
					locationOptions={locationOptions}
					purchasedFromOptions={purchasedFromOptions}
					equipment={printEquipment}
					getEmployeeDisplay={getEmployeeDisplay}
					getVehicleDisplay={getVehicleDisplay}
					getServiceStatus={getServiceStatus}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={equipment}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						getRowId={(row) => row.GUID || row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>

			<AddEditEquipmentDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedEquipment(null);
				}}
				onSave={handleSaveEquipment}
				initialData={selectedEquipment}
				statusOptions={statusOptions}
				typeOptions={typeOptions}
				categoryCodeOptions={categoryOptions}
				conditionOptions={conditionOptions}
				divisionOptions={divisionOptions}
				employeeOptions={employees}
				vehicleOptions={vehicles}
				fundingSourceOptions={fundingSources}
				serviceRequirements={serviceRequirements} // {label, value, ptsCodeId, intervalDays, ...}
				assignmentHistory={selectedEquipment?.assignmentHistory || []}
				maintenanceRecords={selectedEquipment?.maintenanceRecords || []}
				onAddMaintenance={handleAddMaintenance}
				onEditMaintenance={handleEditMaintenance}
				canManageMaintenance={true}
				onMaintenanceUpdate={refreshEquipmentData}
			/>

			<EquipmentCheckInOutDialog
				open={openCheckDialog}
				onClose={() => {
					setOpenCheckDialog(false);
					setSelectedEquipment(null);
				}}
				onSave={handleCheckInOut}
				initialData={selectedEquipment}
				employeeOptions={employees}
				vehicleOptions={vehicles}
				statusOptions={statusOptions}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedEquipment(null);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Equipment"
				message="Are you sure you want to delete this equipment? This will also delete all assignment history."
			/>

			<QuickAuditDialog
				open={openAuditDialog}
				onClose={() => setOpenAuditDialog(false)}
				onSave={handleAudit}
				equipment={equipment}
				vehicles={vehicles}
				employees={employees}
				conditionOptions={conditionOptions}
			/>

			<QuickAssignDialog
				open={quickAssignOpen}
				onClose={() => setQuickAssignOpen(false)}
				onSave={handleBulkAssign}
				equipment={equipment}
				vehicles={vehicles}
				employees={employees}
				divisionOptions={divisionOptions}
				statusOptions={statusOptions}
			/>
		</>
	);
};

export default Equipments;
